
import { useState, useCallback, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Profile, Organization } from "../types";

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  organization: Organization | null;
  loading: boolean;
  profileLoaded: boolean;
}

// LocalStorage cache helpers for auth profile and organization
const PROFILE_KEY = (userId: string) => `auth:profile:${userId}`;
const ORG_KEY = (orgId: string) => `auth:organization:${orgId}`;
const safeGet = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};
const hydrateFromCache = (userId: string): { cachedProfile: Profile | null; cachedOrg: Organization | null } => {
  const cachedProfile = safeGet<Profile>(PROFILE_KEY(userId));
  let cachedOrg: Organization | null = null;
  if (cachedProfile?.organization_id) {
    cachedOrg = safeGet<Organization>(ORG_KEY(cachedProfile.organization_id)) || cachedProfile.organization || null;
  }
  return { cachedProfile, cachedOrg };
};
const persistCache = (profile: Profile | null, organization: Organization | null) => {
  if (profile) safeSet(PROFILE_KEY(profile.id), profile);
  if (organization) safeSet(ORG_KEY(organization.id), organization);
};

export const useOptimizedAuth = () => {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    organization: null,
    loading: true,
    profileLoaded: false,
  });

  // Removed useMemo to avoid stale auth context; return raw authState

  // Optimized organization fetch with caching and reduced retries
  const fetchOrganizationWithRetry = useCallback(async (organizationId: string, retries = 1): Promise<Organization | null> => {
    // Check cache first
    const cachedOrg = queryClient.getQueryData(['organization', organizationId]);
    if (cachedOrg) {
      if (import.meta.env.DEV) {
        console.log(`[Auth] Using cached organization data for ${organizationId}`);
      }
      return cachedOrg as Organization;
    }

    if (import.meta.env.DEV) {
      console.log(`[Auth] Fetching organization ${organizationId}`);
    }
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const { data: orgData, error: orgError } = await supabase
          .from("organizations")
          .select("id, name")
          .eq("id", organizationId)
          .maybeSingle();

        if (orgError) {
          if (import.meta.env.DEV) {
            console.error(`[Auth] Organization fetch error (attempt ${attempt + 1}):`, orgError);
          }
          if (attempt === retries) {
            throw orgError;
          }
          // Shorter wait before retry for better performance
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }

        if (!orgData) {
          if (import.meta.env.DEV) {
            console.warn(`[Auth] No organization found with ID: ${organizationId}`);
          }
          return null;
        }

        const organization = {
          id: orgData.id,
          name: orgData.name
        };

        // Cache the organization data
        queryClient.setQueryData(['organization', organizationId], organization, {
          updatedAt: Date.now() + 1000 * 60 * 10 // 10 minute cache
        });

        if (import.meta.env.DEV) {
          console.log(`[Auth] Organization fetched and cached:`, organization);
        }
        
        return organization;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error(`[Auth] Organization fetch attempt ${attempt + 1} failed:`, error);
        }
        if (attempt === retries) {
          break; // Don't throw, just return null for graceful degradation
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return null;
  }, [queryClient]);

  // React to organization_id changes on the profile and fetch organization if missing
  useEffect(() => {
    const orgId = authState.profile?.organization_id;
    const hasOrgMismatch = orgId && (!authState.organization || authState.organization.id !== orgId);
    if (hasOrgMismatch) {
      if (import.meta.env.DEV) {
        console.log('[Auth] Detected organization_id change, refetching organization:', orgId);
      }
      fetchOrganizationWithRetry(orgId!).then((org) => {
        if (org) {
          setAuthState(prev => ({
            ...prev,
            organization: org,
            profile: prev.profile ? { ...prev.profile, organization: org } : prev.profile,
          }));
        }
      }).catch((e) => {
        console.warn('[Auth] Organization refetch failed:', e);
      });
    }
  }, [authState.profile?.organization_id, authState.organization?.id, fetchOrganizationWithRetry]);

  // Cached profile fetch with reduced API calls
  const fetchUserData = useCallback(async (userId: string, email?: string) => {
    try {
      if (import.meta.env.DEV) {
        console.log('[Auth] Fetching optimized user data for:', userId);
      }
      
      // Check if we already have cached profile data in React Query
      const cachedProfile = queryClient.getQueryData(['profile', userId]);
      if (cachedProfile) {
        if (import.meta.env.DEV) {
          console.log('[Auth] Using cached profile data');
        }
        return { 
          profile: cachedProfile as Profile, 
          organization: (cachedProfile as Profile)?.organization || null 
        };
      }
      
      // Fetch profile with minimal data to reduce network load
      const { data: profileData, error: profileError } = await supabase
        .from("user_profiles")
        .select(`
          id,
          first_name,
          last_name,
          avatar_url,
          role,
          theme_preference,
          organization_id
        `)
        .eq("id", userId)
        .maybeSingle();

      if (profileError) {
        console.error('[Auth] Profile fetch error:', profileError);
        throw profileError;
      }

      if (!profileData) {
        console.log('[Auth] No profile found for user:', userId);
        return { profile: null, organization: null };
      }

      console.log('[Auth] Profile data fetched:', profileData);

      // Build the profile object
      const profile: Profile = {
        id: profileData.id,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        avatar_url: profileData.avatar_url,
        role: profileData.role,
        theme_preference: profileData.theme_preference,
        organization_id: profileData.organization_id,
      };

      // Fetch organization data separately with retry logic if organization_id exists
      let organization: Organization | null = null;
      if (profileData.organization_id) {
        try {
          console.log('[Auth] Profile has organization_id:', profileData.organization_id);
          organization = await fetchOrganizationWithRetry(profileData.organization_id);
          
          if (organization) {
            // Add organization to profile for consistency
            profile.organization = organization;
            console.log('[Auth] Organization added to profile:', organization);
          } else {
            console.warn('[Auth] Failed to fetch organization data');
          }
        } catch (orgError) {
          console.error('[Auth] Critical organization fetch error:', orgError);
          // Don't throw here - we still want to return the profile even if org fetch fails
        }
      } else {
        console.log('[Auth] Profile has no organization_id, attempting membership fallback');
        try {
          const { data: memberRow, error: memberError } = await supabase
            .from('organization_members')
            .select('organization_id')
            .eq('user_id', userId)
            .maybeSingle();

          if (memberError) {
            console.warn('[Auth] Membership fallback error:', memberError);
          } else if (memberRow?.organization_id) {
            console.log('[Auth] Found organization via membership:', memberRow.organization_id);
            profile.organization_id = memberRow.organization_id;
            organization = await fetchOrganizationWithRetry(memberRow.organization_id);
            if (organization) {
              profile.organization = organization;
            }
          } else {
            console.log('[Auth] No organization membership found for user');
          }
        } catch (e) {
          console.error('[Auth] Exception during membership fallback:', e);
        }
      }

      // Final fallback: verify org assignment via edge function using email
      if (!organization && email) {
        try {
          console.log('[Auth] Invoking verify-org-assignment edge function');
          const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-org-assignment', {
            body: { email }
          });
          if (verifyError) {
            console.warn('[Auth] verify-org-assignment error:', verifyError);
          } else if (verifyData?.organization) {
            const org = verifyData.organization;
            console.log('[Auth] Edge function returned organization:', org);
            profile.organization_id = org.id;
            organization = await fetchOrganizationWithRetry(org.id);
            if (!organization) {
              // fallback to direct data from edge function
              organization = { id: org.id, name: org.name } as Organization;
            }
            profile.organization = organization || undefined;
          }
        } catch (e) {
          console.error('[Auth] Exception during verify-org-assignment fallback:', e);
        }
      }

      // Cache the resolved profile (including organization if available)
      try {
        queryClient.setQueryData(['profile', userId], profile, {
          updatedAt: Date.now() + 1000 * 60 * 5 // 5 minutes
        });
      } catch (e) {
        // Non-fatal
      }

      console.log('[Auth] Final user data:', { profile, organization });
      persistCache(profile, organization);
      return { profile, organization };
    } catch (error) {
      console.error('[Auth] Error fetching user data:', error);
      return { profile: null, organization: null };
    }
  }, [fetchOrganizationWithRetry, queryClient]);

  // Optimized sign out
  const signOut = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true }));
      
      // Clear state first for immediate UI feedback
      setAuthState({
        user: null,
        session: null,
        profile: null,
        organization: null,
        loading: false,
        profileLoaded: false,
      });

      // Clear query cache
      queryClient.clear();

      // Sign out from Supabase (local scope to avoid logging out other devices)
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;
      
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, [queryClient]);

  // Initialize auth state with timeout protection
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const initializeAuth = async () => {
      try {
        console.log('[Auth] Initializing auth state');
        
        // Initialization timeout safeguard (non-fatal)
        timeoutId = setTimeout(() => {
          if (mounted) {
            if (import.meta.env.DEV) {
              console.warn('[Auth] Initialization taking longer than expected (15s). UI will continue while background fetch proceeds.');
            }
            // Do not mark profile as loaded; allow components to wait on profileLoaded
            setAuthState(prev => ({
              ...prev,
              loading: false,
            }));
          }
        }, 15000);

        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (!mounted) return;

        if (session?.user) {
          console.log('[Auth] Session found, fetching user data');
          setAuthState(prev => ({ 
            ...prev, 
            user: session.user, 
            session,
            loading: true 
          }));

          // Hydrate from local cache for immediate UI
          try {
            const { cachedProfile, cachedOrg } = hydrateFromCache(session.user.id);
            if (cachedProfile) {
              setAuthState(prev => ({
                ...prev,
                profile: cachedProfile,
                organization: cachedOrg,
                loading: false,
                profileLoaded: true,
              }));
              if (import.meta.env.DEV) {
                console.log('[Auth] Hydrated profile/organization from local cache');
              }
            }
          } catch {}
          
          // Invalidate profile/org caches on fresh session
          try {
            queryClient.removeQueries({ queryKey: ['profile'] });
            queryClient.removeQueries({ queryKey: ['organization'] });
            if (import.meta.env.DEV) {
              console.log('[Auth] Cleared profile/organization caches before initial fetch');
            }
          } catch (e) {
            console.warn('[Auth] Cache clear failed (non-fatal):', e);
          }
          
          // Fetch profile data with safer timeout and background retry
          let userData: { profile: Profile | null; organization: Organization | null } | null = null;
          try {
            userData = await Promise.race([
              fetchUserData(session.user.id, session.user.email || undefined),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Profile fetch timeout')), 20000)
              ),
            ]);
          } catch (err) {
            console.warn('[Auth] Timed out fetching profile. Will continue in background.', err);
            // Background refresh without blocking UI
            setTimeout(async () => {
              try {
                const fresh = await fetchUserData(session.user.id, session.user.email || undefined);
                if (!mounted) return;
                setAuthState(prev => ({
                  ...prev,
                  profile: fresh.profile,
                  organization: fresh.organization,
                }));
                persistCache(fresh.profile, fresh.organization);
              } catch (e) {
                console.error('[Auth] Background profile fetch failed:', e);
              }
            }, 0);
            // Ensure UI is not stuck in loading state
            setAuthState(prev => ({ ...prev, loading: false }));
          }
          
          if (!mounted) return;

          clearTimeout(timeoutId);
          
          if (userData) {
            // Update state with explicit organization data
            setAuthState(prev => ({
              ...prev,
              profile: userData.profile,
              organization: userData.organization,
              loading: false,
              profileLoaded: true,
            }));
            persistCache(userData.profile, userData.organization);
            
            console.log('[Auth] Auth state updated:', {
              hasProfile: !!userData.profile,
              hasOrganization: !!userData.organization,
              organizationId: userData.profile?.organization_id,
              organizationName: userData.organization?.name
            });
          }
        } else {
          console.log('[Auth] No session found');
          clearTimeout(timeoutId);
          setAuthState({
            user: null,
            session: null,
            profile: null,
            organization: null,
            loading: false,
            profileLoaded: true,
          });
        }
      } catch (error) {
        console.error('[Auth] Initialization error:', error);
        if (mounted) {
          clearTimeout(timeoutId);
          setAuthState(prev => ({ 
            ...prev, 
            loading: false, 
            profileLoaded: true 
          }));
        }
      }
    };

    // Auth state change listener with enhanced organization handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] State change event:', event);
      if (!mounted) return;

      if (event === 'SIGNED_OUT' || !session?.user) {
        console.log('[Auth] User signed out or no session');
        setAuthState({
          user: null,
          session: null,
          profile: null,
          organization: null,
          loading: false,
          profileLoaded: true,
        });
        queryClient.clear();
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        console.log('[Auth] User signed in or token refreshed');
        setAuthState(prev => ({ 
          ...prev, 
          user: session!.user, 
          session: session!,
          loading: true 
        }));

        // Hydrate from local cache for instant UI
        try {
          const { cachedProfile, cachedOrg } = hydrateFromCache(session!.user.id);
          if (cachedProfile) {
            setAuthState(prev => ({
              ...prev,
              profile: cachedProfile,
              organization: cachedOrg,
              loading: false,
              profileLoaded: true,
            }));
            if (import.meta.env.DEV) {
              console.log('[Auth] Hydrated profile/organization from local cache after auth event');
            }
          }
        } catch {}
        
        // Invalidate profile/org caches on sign-in/refresh

        try {
          queryClient.removeQueries({ queryKey: ['profile'] });
          queryClient.removeQueries({ queryKey: ['organization'] });
          if (import.meta.env.DEV) {
            console.log('[Auth] Cleared profile/organization caches after auth event');
          }
        } catch (e) {
          console.warn('[Auth] Cache clear failed (non-fatal):', e);
        }
        setTimeout(async () => {
          try {
            let userData: { profile: Profile | null; organization: Organization | null } | null = null;
            try {
              userData = await Promise.race([
                fetchUserData(session!.user.id, session!.user.email || undefined),
                new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Profile fetch timeout')), 20000)
                ),
              ]);
            } catch (err) {
              console.warn('[Auth] Timed out fetching profile on auth event. Background refresh will continue.', err);
              // Background refresh
              setTimeout(async () => {
                try {
                  const fresh = await fetchUserData(session!.user.id, session!.user.email || undefined);
                  if (!mounted) return;
                  setAuthState(prev => ({
                    ...prev,
                    profile: fresh.profile,
                    organization: fresh.organization,
                  }));
                  persistCache(fresh.profile, fresh.organization);
                } catch (e) {
                  console.error('[Auth] Background profile fetch after auth event failed:', e);
                }
              }, 0);
              setAuthState(prev => ({ ...prev, loading: false }));
            }

            if (!mounted) return;

            if (userData) {
              setAuthState(prev => ({
                ...prev,
                profile: userData.profile,
                organization: userData.organization,
                loading: false,
                profileLoaded: true,
              }));
              persistCache(userData.profile, userData.organization);
              console.log('[Auth] Auth state updated after event:', {
                event,
                hasProfile: !!userData.profile,
                hasOrganization: !!userData.organization,
                organizationId: userData.profile?.organization_id
              });
            }
          } catch (error) {
            console.error('[Auth] Error fetching user data on auth change:', error);
            if (mounted) {
              setAuthState(prev => ({
                ...prev,
                loading: false,
                profileLoaded: true,
              }));
            }
          }
        }, 0);
      }
    });

    initializeAuth();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchUserData, queryClient]);

  return {
    ...authState,
    signOut,
  };
};
